import Button from "@/components/bricks/button/Button";
import { useCombobox, useMultipleSelection } from "downshift";
import { Reorder } from "motion/react";
import { useContext, useState } from "react";
import { listFilter } from "../../util/filter";
import { IColumn } from "@/interfaces/table";
import { ModalContext } from "../Modal/Modal";
import SearchIcon from "../icons/SearchIcon";
import PlusIcon from "../icons/PlusIcon";
import CheckIcon from "../icons/CheckIcon";
import DragIndicatorIcon from "../icons/DragIndicatorIcon";
import CloseIcon from "../icons/CloseIcon";

export default function ColumnEditor<T>(props: {
  defaultColumns: IColumn<T>[];
  columns: IColumn<T>[];
  setActiveColumns: React.Dispatch<React.SetStateAction<T[]>>;
}) {
  const { setModalOpen } = useContext(ModalContext);
  const { columns, defaultColumns, setActiveColumns } = props;

  const [autocompleteItems, setAutocompleteItems] = useState<IColumn<T>[]>(
    defaultColumns
      .map((item) => item)
      .sort((a, b) => a.title.localeCompare(b.title)),
  );

  const {
    getSelectedItemProps,
    getDropdownProps,
    addSelectedItem,
    removeSelectedItem,
    selectedItems,
    setSelectedItems,
  } = useMultipleSelection({
    initialSelectedItems: columns.filter((i) => i.active),
    stateReducer(state, { type, changes }) {
      switch (type) {
        // Default behavior is that selected items are deleted on backspace,
        // so we override it to do nothing.
        case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
          return state;
        default:
          return changes;
      }
    },
  });
  const {
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    items: autocompleteItems,
    itemToString(item) {
      return item ? item.title : "";
    },
    // defaultHighlightedIndex: 0, // after selection, highlight the first item.
    selectedItem: null,
    stateReducer(_, { type, changes }) {
      switch (type) {
        case useCombobox.stateChangeTypes.InputChange:
          setAutocompleteItems(
            defaultColumns
              .filter((item) => listFilter(item, ["title"], changes.inputValue))
              .sort((a, b) => a.title.localeCompare(b.title)),
          );
          return changes;
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          if (changes.selectedItem) {
            const existingItem = selectedItems.find(
              (i) => i.title === changes.selectedItem?.title,
            );

            // add or remove item
            if (!existingItem) {
              addSelectedItem(changes.selectedItem);
            } else {
              removeSelectedItem(existingItem);
            }

            // reset filter
            setAutocompleteItems(
              defaultColumns
                .filter((item) => listFilter(item, ["title"], ""))
                .sort((a, b) => a.title.localeCompare(b.title)),
            );

            return {
              ...changes,
              inputValue: "",
            };
          }
          return changes;
        default:
          return changes;
      }
    },
  });

  return (
    <div>
      <div className={"c-column-editor"}>
        <div className={"c-column-editor-leftpane"}>
          <div className="c-column-editor-leftpane-top">
            <label {...getLabelProps()}>
              <h3>Tilføj kolonner</h3>
            </label>
            <div>
              <div className="c-search--column-editor position-relative">
                <span className="c-search__icon text-secondary">
                  <SearchIcon />
                </span>
                <input
                  className="c-search__input form-control"
                  type={"search"}
                  placeholder="Søg kolonne ..."
                  {...getInputProps(
                    getDropdownProps({ preventKeyAction: false }),
                  )}
                />
              </div>
            </div>
          </div>
          <ul {...getMenuProps()}>
            {autocompleteItems.map((item, index) => {
              const isActive =
                selectedItems.find((i) => i.field === item.field) !== undefined;
              const isHighligted = highlightedIndex === index;
              const classNames = `${isActive ? "is-active" : ""}`;
              return (
                <li
                  className={`${classNames}${
                    isHighligted ? " border" : " border border-white"
                  }`}
                  key={`${item.field}${index}`}
                  {...getItemProps({ item, index })}
                >
                  <span>{item.title}</span>
                  <span>
                    {isHighligted && !isActive && <PlusIcon />}
                    {isActive && <CheckIcon width={20} />}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
        <div className={"c-column-editor-rightpane"}>
          <div className="c-column-editor-rightpane-top">
            <h3>Valgte kolonner</h3>
            <p className="text-muted">Træk for at omarrangere</p>
          </div>
          <Reorder.Group
            axis="y"
            values={selectedItems}
            onReorder={setSelectedItems}
            as="div"
            layoutScroll
            {...{ className: "reorder-group" }}
          >
            {selectedItems.map(
              function renderSelectedItem(selectedItem, index) {
                return (
                  <Reorder.Item
                    as="div"
                    value={selectedItem}
                    className="c-column-editor-item"
                    key={`selected-item-${selectedItem.field}`}
                    {...getSelectedItemProps({ selectedItem, index })}
                    tabIndex={-1}
                  >
                    <div className="c-column-editor-item-index">
                      {index + 1}.
                    </div>
                    <div className="c-column-editor-item-pill">
                      <DragIndicatorIcon />
                      <span>{selectedItem.title}</span>
                      {selectedItems.length > 1 && (
                        <button
                          className="btn-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeSelectedItem(selectedItem);
                          }}
                        >
                          <CloseIcon height={18} />
                        </button>
                      )}
                    </div>
                  </Reorder.Item>
                );
              },
            )}
          </Reorder.Group>
        </div>
      </div>
      <div className="c-modal-footer">
        <Button
          color="secondary"
          onClick={() => {
            setSelectedItems(defaultColumns.filter((c) => c.active));
          }}
        >
          Nulstil
        </Button>
        <div style={{ marginLeft: "auto" }}>
          <Button
            color="secondary"
            onClick={() => {
              setModalOpen(false);
            }}
          >
            Annuller
          </Button>
        </div>
        <Button
          onClick={() => {
            setActiveColumns(selectedItems.map((c) => c.field));
            setModalOpen(false);
          }}
        >
          Anvend
        </Button>
      </div>
    </div>
  );
}
